import React, { useState } from "react";
import axios from "axios";
import { toast } from "sonner";
import { useLanguage } from "@/context/LanguageContext";
import { MapPin, Phone, Clock, ShoppingBag, Truck } from "lucide-react";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const SERVICE_ICONS = [ShoppingBag, Truck];

export default function VisitContact() {
  const { t, language } = useLanguage();
  const [form, setForm] = useState({ name: "", email: "", phone: "", message: "" });
  const [submitting, setSubmitting] = useState(false);

  const onChange = (e) => setForm((f) => ({ ...f, [e.target.name]: e.target.value }));

  const submit = async (e) => {
    e.preventDefault();
    if (!form.name || !form.email || !form.message) {
      toast.error(t("visit.errorTitle"), { description: t("visit.errorDesc") });
      return;
    }
    setSubmitting(true);
    try {
      await axios.post(`${API}/contact`, { ...form, language });
      toast.success(t("visit.successTitle"), { description: t("visit.successDesc") });
      setForm({ name: "", email: "", phone: "", message: "" });
    } catch (err) {
      toast.error(t("visit.errorTitle"), { description: t("visit.errorDesc") });
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <section id="visit" data-testid="visit-section" className="relative bg-[#232321] py-24 md:py-36 text-[#F9F6F0]">
      <div className="mx-auto max-w-7xl px-6 md:px-10">
        <div className="max-w-3xl mb-14">
          <span className="text-[12px] uppercase tracking-[0.22em] text-[#E6A85C] font-medium">
            {t("visit.eyebrow")}
          </span>
          <h2 className="font-serif-display text-4xl sm:text-5xl lg:text-6xl tracking-tight leading-[1.02] mt-4">
            {t("visit.title")}
          </h2>
        </div>

        <div className="grid lg:grid-cols-12 gap-10 lg:gap-14">
          {/* Info card */}
          <div className="lg:col-span-5 space-y-8">
            <div className="rounded-2xl border border-[#F9F6F0]/15 bg-[#F9F6F0]/[0.03] backdrop-blur-md p-7 space-y-7">
              <div className="flex gap-4">
                <div className="h-10 w-10 rounded-full bg-[#C05A3A]/20 border border-[#C05A3A]/40 flex items-center justify-center shrink-0">
                  <MapPin className="h-5 w-5 text-[#E6A85C]" strokeWidth={1.5} />
                </div>
                <div>
                  <div className="text-[11px] uppercase tracking-[0.22em] text-[#F9F6F0]/60">
                    Address
                  </div>
                  <div data-testid="visit-address" className="mt-1 font-serif-display text-xl leading-snug">
                    {t("visit.address")}
                  </div>
                  <div className="text-sm text-[#F9F6F0]/65 mt-1">{t("visit.located")}</div>
                </div>
              </div>

              <div className="flex gap-4">
                <div className="h-10 w-10 rounded-full bg-[#C05A3A]/20 border border-[#C05A3A]/40 flex items-center justify-center shrink-0">
                  <Phone className="h-5 w-5 text-[#E6A85C]" strokeWidth={1.5} />
                </div>
                <div>
                  <div className="text-[11px] uppercase tracking-[0.22em] text-[#F9F6F0]/60">
                    {t("visit.phoneLabel")}
                  </div>
                  <a
                    href="tel:+12059837363"
                    data-testid="visit-phone"
                    className="mt-1 font-serif-display text-xl block hover:text-[#E6A85C] transition-colors"
                  >
                    {t("visit.phone")}
                  </a>
                </div>
              </div>

              <div className="flex gap-4">
                <div className="h-10 w-10 rounded-full bg-[#C05A3A]/20 border border-[#C05A3A]/40 flex items-center justify-center shrink-0">
                  <Clock className="h-5 w-5 text-[#E6A85C]" strokeWidth={1.5} />
                </div>
                <div className="flex-1">
                  <div className="text-[11px] uppercase tracking-[0.22em] text-[#F9F6F0]/60">
                    {t("visit.hoursLabel")}
                  </div>
                  <ul className="mt-2 space-y-1.5">
                    {t("visit.hours").map((h) => (
                      <li key={h.day} className="flex items-baseline justify-between text-[15px]">
                        <span className="text-[#F9F6F0]/75">{h.day}</span>
                        <span className="font-medium">{h.time}</span>
                      </li>
                    ))}
                  </ul>
                </div>
              </div>

              <div className="flex flex-wrap gap-2 pt-2 border-t border-[#F9F6F0]/10">
                {t("visit.services").map((s, i) => {
                  const Icon = SERVICE_ICONS[i] || ShoppingBag;
                  return (
                    <span
                      key={s}
                      className="inline-flex items-center gap-2 rounded-full bg-[#F9F6F0]/8 border border-[#F9F6F0]/15 px-3 py-1.5 text-[12px] text-[#F9F6F0]/85"
                    >
                      <Icon className="h-3.5 w-3.5" strokeWidth={1.5} /> {s}
                    </span>
                  );
                })}
              </div>

              <a
                data-testid="visit-directions-cta"
                href="https://www.google.com/maps/dir/?api=1&destination=La+Hacienda+Michoacan+Supermarket+118+Green+Springs+Hwy+Birmingham+AL"
                target="_blank"
                rel="noreferrer"
                className="inline-flex w-full items-center justify-center rounded-full bg-[#C05A3A] hover:bg-[#A54C30] text-[#F9F6F0] px-6 py-3 text-[14px] font-medium tracking-wide transition-all hover:-translate-y-0.5"
              >
                {t("visit.directions")}
              </a>
            </div>

            {/* Map */}
            <div className="rounded-2xl overflow-hidden border border-[#F9F6F0]/15 h-[260px]">
              <iframe
                title="map"
                data-testid="visit-map"
                src="https://www.google.com/maps?q=118+Green+Springs+Hwy+Birmingham+AL+35209&output=embed"
                width="100%"
                height="100%"
                style={{ border: 0, filter: "grayscale(0.2) contrast(1.05)" }}
                loading="lazy"
                referrerPolicy="no-referrer-when-downgrade"
              />
            </div>
          </div>

          {/* Contact form */}
          <div className="lg:col-span-7">
            <div className="rounded-2xl border border-[#F9F6F0]/15 bg-[#F9F6F0]/[0.03] backdrop-blur-md p-7 md:p-10">
              <h3 className="font-serif-display text-2xl md:text-3xl">{t("visit.formTitle")}</h3>
              <p className="text-sm text-[#F9F6F0]/70 mt-2">{t("visit.formSubtitle")}</p>

              <form onSubmit={submit} data-testid="contact-form" className="mt-8 grid grid-cols-1 sm:grid-cols-2 gap-5">
                <div className="sm:col-span-1">
                  <label className="text-[11px] uppercase tracking-[0.22em] text-[#F9F6F0]/60">
                    {t("visit.name")}
                  </label>
                  <input
                    data-testid="contact-name-input"
                    name="name"
                    value={form.name}
                    onChange={onChange}
                    required
                    className="mt-2 w-full bg-transparent border-b border-[#F9F6F0]/25 focus:border-[#E6A85C] outline-none py-2 text-[#F9F6F0] placeholder:text-[#F9F6F0]/30"
                    placeholder="María"
                  />
                </div>
                <div className="sm:col-span-1">
                  <label className="text-[11px] uppercase tracking-[0.22em] text-[#F9F6F0]/60">
                    {t("visit.email")}
                  </label>
                  <input
                    data-testid="contact-email-input"
                    name="email"
                    type="email"
                    value={form.email}
                    onChange={onChange}
                    required
                    className="mt-2 w-full bg-transparent border-b border-[#F9F6F0]/25 focus:border-[#E6A85C] outline-none py-2 text-[#F9F6F0] placeholder:text-[#F9F6F0]/30"
                    placeholder="maria@email.com"
                  />
                </div>
                <div className="sm:col-span-2">
                  <label className="text-[11px] uppercase tracking-[0.22em] text-[#F9F6F0]/60">
                    {t("visit.phoneOpt")}
                  </label>
                  <input
                    data-testid="contact-phone-input"
                    name="phone"
                    value={form.phone}
                    onChange={onChange}
                    className="mt-2 w-full bg-transparent border-b border-[#F9F6F0]/25 focus:border-[#E6A85C] outline-none py-2 text-[#F9F6F0] placeholder:text-[#F9F6F0]/30"
                    placeholder="(205) 555-0123"
                  />
                </div>
                <div className="sm:col-span-2">
                  <label className="text-[11px] uppercase tracking-[0.22em] text-[#F9F6F0]/60">
                    {t("visit.message")}
                  </label>
                  <textarea
                    data-testid="contact-message-input"
                    name="message"
                    rows={5}
                    value={form.message}
                    onChange={onChange}
                    required
                    className="mt-2 w-full bg-transparent border-b border-[#F9F6F0]/25 focus:border-[#E6A85C] outline-none py-2 text-[#F9F6F0] placeholder:text-[#F9F6F0]/30 resize-none"
                    placeholder="…"
                  />
                </div>

                <div className="sm:col-span-2 flex items-center justify-between gap-4 pt-2">
                  <p className="text-[12px] text-[#F9F6F0]/55 max-w-sm">
                    {language === "es"
                      ? "Tus datos solo se usan para contestarte."
                      : "Your details are used only to reply to you."}
                  </p>
                  <button
                    data-testid="contact-submit-btn"
                    type="submit"
                    disabled={submitting}
                    className="rounded-full bg-[#E6A85C] hover:bg-[#d39849] disabled:opacity-60 disabled:cursor-not-allowed text-[#232321] px-7 py-3 text-[13px] font-semibold tracking-wide transition-all hover:-translate-y-0.5"
                  >
                    {submitting ? t("visit.sending") : t("visit.submit")}
                  </button>
                </div>
              </form>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
}
